// src/customers.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
import TitleBar from './components/ui/TitleBar';
import BackToDashboardButton from './components/ui/BackToDashboardButton';
import AddressAutocomplete from './AddressAutocomplete';
import NotificationOverlayFrame from './components/NotificationOverlayFrame';

/* ===============================
   HELPERS
================================== */

const fetchNoCache = async (url) => {
  const res = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache', ...getTenantHeaders() }
  });
  return res;
};

const formatPhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const digitsOnly10 = (value) => String(value || '').replace(/\D/g, '').slice(0, 10);

const normalizePhoneTarget = (value) => {
  const raw = String(value || '').trim();
  const normalized = raw.startsWith('+')
    ? `+${raw.slice(1).replace(/\D/g, '')}`
    : raw.replace(/\D/g, '');
  const digitCount = normalized.replace(/\D/g, '').length;
  return digitCount >= 7 && digitCount <= 15 ? normalized : '';
};

const buildAddress = ({ street, city, state, zip }) => {
  const s = String(street || '').trim();
  const c = String(city || '').trim();
  const st = String(state || '').trim();
  const z = String(zip || '').trim();
  const line2 = [c, st, z].filter(Boolean).join(' ');
  return [s, line2].filter(Boolean).join(', ');
};

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/* ===============================
   COMPONENT
================================== */
const Customers = ({ navigate } = {}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [customers, setCustomers] = useState([]);

  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [loadingCustomerInvoices, setLoadingCustomerInvoices] = useState(false);
  const [customerInvoiceError, setCustomerInvoiceError] = useState('');
  const [showCallChooser, setShowCallChooser] = useState(false);
  const customerInvoiceRequestRef = useRef(0);

  const [errorMsg, setErrorMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [statusNotice, setStatusNotice] = useState(null);
  const [updateErrorNotice, setUpdateErrorNotice] = useState('');
  const [originalArchivedStatus, setOriginalArchivedStatus] = useState(null);
  const statusNoticeTimerRef = useRef(null);

  // ✅ ENTRY FIELDS MUST MATCH QUICK ADD CUSTOMER
  const [formData, setFormData] = useState({
    id: null,
    company: '',
    contact: '',
    email: '',
    office: '',
    mobile: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    notes: '',
    terms: 'COD',
    is_archived: 0
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const API = useMemo(() => getApiBase(), []);

  const loadCustomers = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const ts = Date.now();
      const res = await fetchNoCache(`${API}/api/customers?status=all&ts=${ts}`);
      if (!res.ok) throw new Error(`Customers fetch failed (${res.status})`);
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErrorMsg('ERROR LOADING CUSTOMERS.');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    if (statusNoticeTimerRef.current) window.clearTimeout(statusNoticeTimerRef.current);
  }, []);

  const resetForm = () => {
    setFormData({
      id: null,
      company: '',
      contact: '',
      email: '',
      office: '',
      mobile: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      notes: '',
      terms: 'COD',
      is_archived: 0
    });
    setOriginalArchivedStatus(null);
  };

  const showSuccessNotice = (message, accentColor, messageColor = accentColor) => {
    setStatusNotice({ message, accentColor, messageColor });
    if (statusNoticeTimerRef.current) window.clearTimeout(statusNoticeTimerRef.current);
    statusNoticeTimerRef.current = window.setTimeout(() => setStatusNotice(null), 2500);
  };

  // DB -> Form (compat for older rows)
  const normalizeFromDb = (c) => {
    const mobileFallback = c?.mobile_phone ?? c?.phone ?? '';
    const officeFallback = c?.office_phone ?? '';
    const streetFallback = c?.street ?? c?.address ?? '';

    return {
      id: c?.id ?? null,
      company: c?.name ?? '',
      contact: c?.contactPerson ?? '',
      email: c?.billingEmail ?? c?.email ?? '',
      office: formatPhone(officeFallback),
      mobile: formatPhone(mobileFallback),
      street: streetFallback ?? '',
      city: c?.city ?? '',
      state: c?.state ?? '',
      zip: c?.zip ?? '',
      notes: c?.notes ?? '',
      terms: c?.terms ?? 'COD',
      is_archived: Number(c?.is_archived || 0) === 1 ? 1 : 0
    };
  };

  // Form -> DB payload (write split fields + legacy address/phone)
  const buildDbPayload = (f) => {
    const payload = {
      name: String(f.company || f.contact || "").trim(),
      contactPerson: String(f.contact || '').trim(),
      billingEmail: String(f.email || '').trim(),

      office_phone: digitsOnly10(f.office),
      mobile_phone: digitsOnly10(f.mobile),

      street: String(f.street || '').trim(),
      city: String(f.city || '').trim(),
      state: String(f.state || '').trim().toUpperCase(),
      zip: String(f.zip || '').trim(),

      terms: String(f.terms || 'COD').trim(),
      notes: String(f.notes || '').trim()
    };

    payload.address = buildAddress(payload);
    payload.phone = payload.mobile_phone || payload.office_phone || null;

    return payload;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'office' || name === 'mobile') {
      setFormData((prev) => ({ ...prev, [name]: formatPhone(value) }));
      return;
    }
    if (name === 'state') {
      setFormData((prev) => ({ ...prev, [name]: String(value || '').toUpperCase().slice(0, 2) }));
      return;
    }
    if (name === 'zip') {
      setFormData((prev) => ({ ...prev, [name]: String(value || '').replace(/\D/g, '').slice(0, 10) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEdit = !!formData.id;
    setErrorMsg('');
    setStatusNotice(null);
    setUpdateErrorNotice('');
    if (statusNoticeTimerRef.current) window.clearTimeout(statusNoticeTimerRef.current);

    try {
      const payload = buildDbPayload(formData);

      if (!payload.name && !payload.contactPerson) {
        alert('Please enter at least a COMPANY or CONTACT.');
        return;
      }

      const res = await fetch(
        isEdit ? `${API}/api/customers/${formData.id}` : `${API}/api/customers`,
        {
          method: isEdit ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Save failed (${res.status}) ${txt}`);
      }

      const selectedArchivedStatus = Number(formData.is_archived || 0) === 1 ? 1 : 0;
      const statusChanged = isEdit && selectedArchivedStatus !== originalArchivedStatus;
      if (statusChanged) {
        const statusRes = await fetch(`${API}/api/customers/${formData.id}/status`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify({ active: selectedArchivedStatus === 0 })
        });
        const statusData = await statusRes.json().catch(() => null);
        if (!statusRes.ok || statusData?.ok !== true) {
          setUpdateErrorNotice('CUSTOMER DETAILS SAVED, BUT STATUS UPDATE FAILED.');
          return;
        }

        showSuccessNotice(
          statusData.active === true ? 'ACTIVE SET' : 'INACTIVE SET',
          statusData.active === true ? '#39FF14' : '#FFC107'
        );
      } else {
        showSuccessNotice(isEdit ? 'UPDATED' : 'SAVED', '#B78F7A', '#E8E2DA');
      }

      resetForm();
      await loadCustomers();
      window.scrollTo(0, 0);
    } catch (e) {
      console.error(e);
      if (isEdit) setUpdateErrorNotice('SAVE FAILED. CHECK BACKEND / API.');
      else setErrorMsg('SAVE FAILED. CHECK BACKEND / API.');
    }
  };

  const filteredCustomers = useMemo(() => {
    const q = String(searchTerm || '').toLowerCase().trim();
    const statusCustomers = (customers || []).filter((customer) => {
      const inactive = Number(customer?.is_archived || 0) === 1;
      if (statusFilter === 'inactive') return inactive;
      if (statusFilter === 'all') return true;
      return !inactive;
    });
    if (!q) return statusCustomers;

    const qDigits = q.replace(/\D/g, '');

    return statusCustomers.filter((c) => {
      const company = String(c?.name || '').toLowerCase();
      const contact = String(c?.contactPerson || '').toLowerCase();
      const billingEmail = String(c?.billingEmail || c?.email || '').toLowerCase();

      const office = String(c?.office_phone || '');
      const mobile = String(c?.mobile_phone || c?.phone || '');

      return (
        company.includes(q) ||
        contact.includes(q) ||
        billingEmail.includes(q) ||
        (qDigits && (office.includes(qDigits) || mobile.includes(qDigits)))
      );
    });
  }, [customers, searchTerm, statusFilter]);

  const customerRevenue = (custRow) => Number(custRow?.total_revenue || 0);

  const preferredPhone = (c) => formatPhone(c?.mobile_phone || c?.phone || c?.office_phone || '');

  const normalizedContactMobile = normalizePhoneTarget(
    viewingCustomer?.mobile_phone || viewingCustomer?.phone
  );
  const textCustomerHref = normalizedContactMobile ? `sms:${normalizedContactMobile}` : '';
  const customerEmail = String(
    viewingCustomer?.billingEmail || viewingCustomer?.email || ''
  ).trim();
  const emailCustomerHref = customerEmail ? `mailto:${customerEmail}` : '';
  const officeCallTarget = normalizePhoneTarget(viewingCustomer?.office_phone);
  const mobileCallTarget = normalizePhoneTarget(
    viewingCustomer?.mobile_phone || viewingCustomer?.phone
  );

  const handleTextCustomer = () => {
    if (!textCustomerHref) {
      alert("Mobile number doesn't exist.");
      return;
    }
    window.location.href = textCustomerHref;
  };

  const handleEmailCustomer = () => {
    if (!emailCustomerHref) {
      alert("Email address doesn't exist.");
      return;
    }
    window.location.href = emailCustomerHref;
  };

  const handleCallCustomer = () => {
    if (!officeCallTarget && !mobileCallTarget) {
      alert("Phone number doesn't exist.");
      return;
    }
    if (officeCallTarget && mobileCallTarget && officeCallTarget !== mobileCallTarget) {
      setShowCallChooser(true);
      return;
    }
    window.location.href = `tel:${mobileCallTarget || officeCallTarget}`;
  };

  const callNumber = (phoneTarget) => {
    setShowCallChooser(false);
    window.location.href = `tel:${phoneTarget}`;
  };

  const viewCustomer = async (customer) => {
    const requestId = ++customerInvoiceRequestRef.current;
    setShowCallChooser(false);
    setViewingCustomer(customer);
    setCustomerInvoices([]);
    setCustomerInvoiceError('');
    setLoadingCustomerInvoices(true);
    try {
      const res = await fetchNoCache(`${API}/api/customers/${customer.id}/invoices?ts=${Date.now()}`);
      if (!res.ok) throw new Error(`Customer invoices fetch failed (${res.status})`);
      const data = await res.json();
      if (requestId !== customerInvoiceRequestRef.current) return;
      setCustomerInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      if (requestId !== customerInvoiceRequestRef.current) return;
      console.error(e);
      setCustomerInvoiceError('ERROR LOADING INVOICES.');
      setCustomerInvoices([]);
    } finally {
      if (requestId === customerInvoiceRequestRef.current) {
        setLoadingCustomerInvoices(false);
      }
    }
  };

  const openInvoiceView = (invoice) => {
    const openInvoiceId = invoice?.external_id || invoice?.id;
    if (!openInvoiceId || typeof navigate !== 'function') {
      alert('Invoice view is unavailable.');
      return;
    }
    navigate('invoices', { openInvoiceId, openInvoiceMode: 'print' });
  };

  // ✅ Colors
  const NEON_PINK = '#FFC107';
  const INNER_NAVY = '#0a1128';
  const CUSTOMER_ACCENT = '#754C38';

  // ✅ Hover: actual color change + keep lift
  const hoverPink = (e) => {
    const el = e.currentTarget;

    if (!el.dataset._bg) el.dataset._bg = el.style.backgroundColor || '';
    if (!el.dataset._color) el.dataset._color = el.style.color || '';
    if (!el.dataset._border) el.dataset._border = el.style.borderColor || '';

    el.style.backgroundColor = NEON_PINK;
    el.style.color = 'black';
    el.style.borderColor = 'black';
    el.style.transform = 'translateY(-1px)';
    el.style.filter = 'brightness(1.05)';
  };
  const hoverOut = (e) => {
    const el = e.currentTarget;
    el.style.backgroundColor = el.dataset._bg || '';
    el.style.color = el.dataset._color || '';
    el.style.borderColor = el.dataset._border || '';
    el.style.transform = 'translateY(0)';
    el.style.filter = 'none';
  };

  const styles = {
    page: { padding: isMobile ? '15px' : '20px' },

    formWrap: {
      backgroundColor: '#B78F7A',
      border: '8px solid black',
      boxShadow: `inset 0 0 0 6px ${CUSTOMER_ACCENT}`,
      padding: isMobile ? '15px' : '30px',
      marginBottom: '22px'
    },
    formGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px' },

    input: {
      padding: '15px',
      border: '4px solid black',
      fontWeight: 900,
      fontSize: '16px',
      backgroundColor: 'white',
      width: '100%',
      boxSizing: 'border-box'
    },

    rowAddr: {
      gridColumn: isMobile ? 'auto' : 'span 2',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : '2fr 1.2fr 0.6fr 1fr',
      gap: '10px'
    },

    full: { gridColumn: isMobile ? 'auto' : 'span 2' },

    mainBtn: {
      gridColumn: isMobile ? 'auto' : 'span 2',
      padding: '18px',
      backgroundColor: 'black',
      color: '#FFA500',
      fontWeight: 900,
      border: '4px solid #E8761D',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      transition: 'filter 0.12s ease, transform 0.12s ease, border-color 0.12s ease'
    },

    msgBad: { marginTop: '10px', color: '#b00020', fontWeight: 900, textAlign: 'center' },

    searchWrap: {
      width: '100%',
      padding: '6px',
      marginBottom: '14px',
      backgroundColor: '#B78F7A',
      boxSizing: 'border-box'
    },

    searchBar: {
      width: '100%',
      padding: '15px',
      border: '8px solid black',
      fontSize: '16px',
      fontWeight: 900,
      textTransform: 'uppercase',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    },

    tableWrapper: {
      width: '100%',
      maxWidth: '100%',
      overflowX: 'auto',
      border: '8px solid black',
      boxShadow: `inset 0 0 0 6px ${INNER_NAVY}`,
      backgroundColor: 'white',
      boxSizing: 'border-box'
    },
    table: { width: '100%', borderCollapse: 'collapse' },

    th: {
      backgroundColor: '#0a1128',
      color: '#E8761D',
      padding: '12px',
      borderBottom: '4px solid black',
      textAlign: 'left',
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '14px'
    },
    thSep: { borderRight: '4px solid black' },

    td: {
      padding: '10px',
      borderTop: '2px solid black',
      borderRight: '2px solid black',
      fontWeight: 800,
      verticalAlign: 'top',
      fontSize: '14px',
      whiteSpace: 'normal',
      wordBreak: 'break-word'
    },

    revenueTd: { color: '#054d3b', whiteSpace: 'nowrap' },

    customerList: {
      display: 'grid',
      gap: '14px',
      width: '100%',
      border: '10px solid black',
      padding: '12px',
      backgroundColor: '#B78F7A',
      boxSizing: 'border-box'
    },
    customerCard: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '14px' : '24px',
      padding: isMobile ? '16px' : '18px 20px',
      border: '8px solid black',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      width: '100%',
      minWidth: 0
    },
    customerInfo: { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' },
    customerName: { display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0, fontSize: '18px', fontWeight: 900, marginBottom: '8px', whiteSpace: 'nowrap' },
    statusBadge: { width: '18px', height: '18px', flex: '0 0 18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 900, lineHeight: 1 },
    customerDetails: { display: 'grid', gap: '4px', fontSize: '14px', fontWeight: 800 },
    customerRevenue: { color: '#054d3b', fontWeight: 900, marginTop: '6px', fontSize: 'clamp(13px, 4vw, 16px)', whiteSpace: 'nowrap' },
    actionCell: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      flex: '0 0 auto',
      maxWidth: '100%'
    },
    actionBtnBase: {
      padding: '10px 12px',
      border: '4px solid black',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      transition: 'filter 0.12s ease, transform 0.12s ease, border-color 0.12s ease'
    },
    greenBtn: { backgroundColor: 'green', color: 'white' },
    blueBtn: { backgroundColor: 'blue', color: 'white' },

    overlay: { display: viewingCustomer ? 'block' : 'none', position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1999 },
    viewModal: {
      position: 'fixed',
      top: '10%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: isMobile ? '92%' : '720px',
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '18px',
      zIndex: 2000,
      boxShadow: '15px 15px 0px black',
      maxHeight: '80vh',
      overflowY: 'auto'
    },

    contactActions: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
      gap: '10px',
      marginBottom: '18px'
    },
    contactActionBtn: {
      gridColumn: 'auto',
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box'
    },
    callChooserOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 2100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      backgroundColor: 'rgba(0,0,0,0.65)'
    },
    callChooser: {
      width: '100%',
      maxWidth: '420px',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      border: '8px solid black',
      boxShadow: '0 0 0 6px #E8761D',
      padding: '18px'
    },

    viewTitle: { marginTop: 0, textTransform: 'uppercase', borderBottom: '4px solid black', paddingBottom: '8px' }
  };

  return (
    <div className="jfn-customers-page" style={styles.page}>
      <style>{`
        @media (max-width: 767px) {
          .jfn-customers-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
      `}</style>
      {statusNotice && (
        <NotificationOverlayFrame
          accentColor={statusNotice.accentColor}
          messageColor={statusNotice.messageColor}
          role="status"
          ariaLive="polite"
          ariaAtomic="true"
        >
          <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 2.2vw, 24px)', lineHeight: 1.35 }}>
            {statusNotice.message}
          </div>
        </NotificationOverlayFrame>
      )}
      {updateErrorNotice && (
        <NotificationOverlayFrame
          accentColor="#FF0000"
          role="alert"
          ariaLive="assertive"
          ariaAtomic="true"
        >
          <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 2.2vw, 24px)', lineHeight: 1.35 }}>
            {updateErrorNotice}
          </div>
        </NotificationOverlayFrame>
      )}
      <div style={styles.overlay} onClick={() => { setShowCallChooser(false); setViewingCustomer(null); }} />

      <TitleBar
        icon="👤"
        title="CUSTOMERS"
        accent={CUSTOMER_ACCENT}
        right={
          <BackToDashboardButton
            hoverColor="#FFC107"
            onClick={() => (window.location.href = '/dashboard')}
          />
        }
      />

      {viewingCustomer && (
        <div style={styles.viewModal}>
          <h2 style={styles.viewTitle}>
            {String(viewingCustomer?.contactPerson || '').trim() || '(NO CONTACT)'}
          </h2>
          <p><strong>COMPANY:</strong> {String(viewingCustomer?.name || '').trim() || '(RESIDENTIAL / N/A)'}</p>
          <p><strong>EMAIL:</strong> {viewingCustomer?.billingEmail || viewingCustomer?.email || ''}</p>
          <p><strong>OFFICE PHONE:</strong> {formatPhone(viewingCustomer?.office_phone || '')}</p>
          <p><strong>MOBILE PHONE:</strong> {formatPhone(viewingCustomer?.mobile_phone || viewingCustomer?.phone || '')}</p>
          <p><strong>STREET:</strong> {viewingCustomer?.street || ''}</p>
          <p><strong>CITY/ST/ZIP:</strong> {[viewingCustomer?.city, viewingCustomer?.state, viewingCustomer?.zip].filter(Boolean).join(' ')}</p>
          <p><strong>TERMS:</strong> {viewingCustomer?.terms || 'COD'}</p>
          <p><strong>NOTES:</strong> {viewingCustomer?.notes || ''}</p>
          <p>
            <strong>TOTAL REVENUE:</strong>{' '}
            <span style={{ color: '#054d3b', fontWeight: 900 }}>
              {money(customerRevenue(viewingCustomer))}
            </span>
          </p>

          <div style={styles.contactActions}>
            <button
              type="button"
              style={{ ...styles.mainBtn, ...styles.contactActionBtn }}
              onMouseEnter={hoverPink}
              onMouseLeave={hoverOut}
              onClick={handleTextCustomer}
            >
              TEXT
            </button>
            <button
              type="button"
              style={{ ...styles.mainBtn, ...styles.contactActionBtn }}
              onMouseEnter={hoverPink}
              onMouseLeave={hoverOut}
              onClick={handleEmailCustomer}
            >
              EMAIL
            </button>
            <button
              type="button"
              style={{ ...styles.mainBtn, ...styles.contactActionBtn }}
              onMouseEnter={hoverPink}
              onMouseLeave={hoverOut}
              onClick={handleCallCustomer}
            >
              CALL
            </button>
          </div>

          {showCallChooser && (
            <div style={styles.callChooserOverlay} onClick={() => setShowCallChooser(false)}>
              <div style={styles.callChooser} onClick={(e) => e.stopPropagation()}>
                <div style={{ ...styles.viewTitle, fontSize: '20px', fontWeight: 900, marginBottom: '14px' }}>
                  CHOOSE PHONE NUMBER
                </div>
                <button
                  type="button"
                  style={{ ...styles.mainBtn, ...styles.contactActionBtn, marginBottom: '10px' }}
                  onMouseEnter={hoverPink}
                  onMouseLeave={hoverOut}
                  onClick={() => callNumber(officeCallTarget)}
                >
                  OFFICE PHONE
                </button>
                <button
                  type="button"
                  style={{ ...styles.mainBtn, ...styles.contactActionBtn, marginBottom: '10px' }}
                  onMouseEnter={hoverPink}
                  onMouseLeave={hoverOut}
                  onClick={() => callNumber(mobileCallTarget)}
                >
                  MOBILE PHONE
                </button>
                <button
                  type="button"
                  style={{ ...styles.mainBtn, ...styles.contactActionBtn, backgroundColor: 'white', color: 'black', borderColor: 'black' }}
                  onMouseEnter={hoverPink}
                  onMouseLeave={hoverOut}
                  onClick={() => setShowCallChooser(false)}
                >
                  CLOSE NOW
                </button>
              </div>
            </div>
          )}

          <h3 style={styles.viewTitle}>INVOICES</h3>
          {loadingCustomerInvoices ? (
            <p><strong>LOADING INVOICES…</strong></p>
          ) : customerInvoiceError ? (
            <p style={{ color: '#b00020', fontWeight: 900 }}>{customerInvoiceError}</p>
          ) : customerInvoices.length === 0 ? (
            <p><strong>No invoices found</strong></p>
          ) : (
            <div style={{ ...styles.tableWrapper, borderWidth: '4px', boxShadow: 'none', marginBottom: '16px' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.thSep }}>INVOICE NUMBER</th>
                    <th style={{ ...styles.th, ...styles.thSep }}>DATE</th>
                    <th style={{ ...styles.th, ...styles.thSep }}>AMOUNT</th>
                    <th style={styles.th}>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {customerInvoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td style={styles.td}>
                        <button
                          type="button"
                          style={{ border: 'none', background: 'none', padding: 0, color: 'blue', fontWeight: 900, textDecoration: 'underline', cursor: 'pointer' }}
                          onClick={() => openInvoiceView(invoice)}
                        >
                          {invoice.invoice_number || '—'}
                        </button>
                      </td>
                      <td style={styles.td}>
                        {invoice.invoice_date
                          ? new Date(`${String(invoice.invoice_date).slice(0, 10)}T00:00:00`).toLocaleDateString()
                          : '—'}
                      </td>
                      <td style={{ ...styles.td, ...styles.revenueTd }}>{money(invoice.total)}</td>
                      <td style={{ ...styles.td, borderRight: 'none' }}>{String(invoice.status || '').toUpperCase()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            style={styles.mainBtn}
            onMouseEnter={hoverPink}
            onMouseLeave={hoverOut}
            onClick={() => { setShowCallChooser(false); setViewingCustomer(null); }}
          >
            CLOSE NOW
          </button>
        </div>
      )}

      {/* ENTRY / EDIT */}
      <div style={styles.formWrap}>
        <form onSubmit={handleSubmit}>
          <div style={styles.formGrid}>
            <input style={styles.input} name="company" placeholder="CUSTOMER NAME / COMPANY" value={formData.company} onChange={handleInputChange} />
            <input style={styles.input} name="contact" placeholder="CONTACT (OPTIONAL)" value={formData.contact} onChange={handleInputChange} />

            <input style={{ ...styles.input, ...styles.full }} name="email" placeholder="EMAIL" value={formData.email} onChange={handleInputChange} />

            <input style={styles.input} name="office" placeholder="OFFICE PHONE" value={formData.office} onChange={handleInputChange} />
            <input style={styles.input} name="mobile" placeholder="MOBILE PHONE" value={formData.mobile} onChange={handleInputChange} />

            <div style={styles.rowAddr}>
              <AddressAutocomplete
                inputStyle={{ ...styles.input, gridColumn: isMobile ? 'span 2' : 'auto' }}
                placeholder="STREET"
                name="street"
                value={formData.street}
                onChange={(v) => setFormData(prev => ({ ...prev, street: v }))}
                onAddressSelect={({ street, city, state, zip }) => {
                  setFormData(prev => ({
                    ...prev,
                    street,
                    city,
                    state: state.slice(0, 2).toUpperCase(),
                    zip,
                  }));
                }}
              />
              <input style={styles.input} name="city" placeholder="CITY" value={formData.city} onChange={handleInputChange} />
              <input style={styles.input} name="state" placeholder="ST" maxLength={2} value={formData.state} onChange={handleInputChange} />
              <input
                style={{ ...styles.input, gridColumn: isMobile ? 'span 2' : 'auto' }}
                name="zip"
                placeholder="ZIP"
                value={formData.zip}
                onChange={handleInputChange}
              />
            </div>

            <select style={{ ...styles.input, ...styles.full }} name="terms" value={formData.terms || 'COD'} onChange={handleInputChange}>
              <option value="COD">TERMS: COD (DUE TODAY)</option>
              <option value="NET15">TERMS: NET 15</option>
              <option value="NET30">TERMS: NET 30</option>
            </select>

            <textarea style={{ ...styles.input, height: '90px', ...styles.full }} name="notes" placeholder="NOTES" value={formData.notes} onChange={handleInputChange} />

            {formData.id && (
              <div style={{
                ...styles.full,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: isMobile ? '4px' : '8px',
                flexWrap: 'nowrap',
                width: '100%',
                boxSizing: 'border-box',
                padding: isMobile ? '10px 6px' : '14px 16px',
                border: '4px solid black',
                backgroundColor: 'white',
                fontWeight: 900
              }}>
                <span style={{ fontSize: isMobile ? 'clamp(9px, 3vw, 11px)' : '15px', whiteSpace: 'nowrap' }}>SET STATUS NOW</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'green', fontSize: isMobile ? 'clamp(9px, 3vw, 11px)' : '15px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={Number(formData.is_archived || 0) === 0}
                    onChange={() => setFormData((current) => ({ ...current, is_archived: 0 }))}
                    style={{ width: isMobile ? 16 : 18, height: isMobile ? 16 : 18, margin: 0, accentColor: 'green' }}
                  />
                  ACTIVE
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'red', fontSize: isMobile ? 'clamp(9px, 3vw, 11px)' : '15px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={Number(formData.is_archived || 0) === 1}
                    onChange={() => setFormData((current) => ({ ...current, is_archived: 1 }))}
                    style={{ width: isMobile ? 16 : 18, height: isMobile ? 16 : 18, margin: 0, accentColor: 'red' }}
                  />
                  INACTIVE
                </label>
              </div>
            )}

            <button style={styles.mainBtn} onMouseEnter={hoverPink} onMouseLeave={hoverOut} type="submit">
              {formData.id ? 'UPDATE CUSTOMER' : 'ADD CUSTOMER TO MYTOOL NOW'}
            </button>
          </div>

          {errorMsg && <div style={styles.msgBad}>{errorMsg}</div>}
        </form>
      </div>

      {/* LIST */}
      <TitleBar
        icon="🔎"
        title="GLOBAL CUSTOMER SEARCH"
        accent={CUSTOMER_ACCENT}
        right={
          <div style={{ fontWeight: 900 }}>
            {loading ? 'LOADING…' : `${filteredCustomers.length} SHOWN`}
          </div>
        }
      />

      <div style={styles.searchWrap}>
        <input
          style={styles.searchBar}
          placeholder="🔍 SEARCH BY NAME / CONTACT / PHONE / EMAIL..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          style={{ ...styles.searchBar, marginTop: '8px' }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Customer status filter"
        >
          <option value="active">ACTIVE CUSTOMERS</option>
          <option value="inactive">INACTIVE CUSTOMERS</option>
          <option value="all">ALL CUSTOMERS</option>
        </select>
      </div>

      <TitleBar
        icon="📋"
        title="CUSTOMER LIST"
        accent={CUSTOMER_ACCENT}
        wrapStyle={{ marginBottom: '12px' }}
      />

      <div style={styles.customerList}>
        {filteredCustomers.map((c) => {
          const contact = String(c.contactPerson || '').trim();
          const phone = preferredPhone(c);
          const email = String(c.billingEmail || c.email || '').trim();

          return (
            <article key={c.id} style={styles.customerCard}>
              <div style={styles.customerInfo}>
                <div style={styles.customerName}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {String(c.name || '').trim() || '(RESIDENTIAL / N/A)'}
                  </span>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: Number(c.is_archived || 0) === 1 ? 'red' : 'green'
                    }}
                    aria-label={Number(c.is_archived || 0) === 1 ? 'Inactive customer' : 'Active customer'}
                    title={Number(c.is_archived || 0) === 1 ? 'Inactive' : 'Active'}
                  >
                    {Number(c.is_archived || 0) === 1 ? 'I' : 'A'}
                  </span>
                </div>
                <div style={styles.customerDetails}>
                  {contact && <div>CONTACT: {contact}</div>}
                  {phone && <div>PHONE: {phone}</div>}
                  {email && <div>EMAIL: {email}</div>}
                </div>
                <div style={styles.customerRevenue}>REVENUE: {money(customerRevenue(c))}</div>
              </div>

              <div style={styles.actionCell}>
                <button
                  type="button"
                  style={{ ...styles.actionBtnBase, ...styles.greenBtn }}
                  onMouseEnter={hoverPink}
                  onMouseLeave={hoverOut}
                  onClick={() => viewCustomer(c)}
                >
                  VIEW
                </button>

                <button
                  type="button"
                  style={{ ...styles.actionBtnBase, ...styles.blueBtn }}
                  onMouseEnter={hoverPink}
                  onMouseLeave={hoverOut}
                  onClick={() => {
                    setFormData(normalizeFromDb(c));
                    setOriginalArchivedStatus(Number(c.is_archived || 0) === 1 ? 1 : 0);
                    window.scrollTo(0, 0);
                  }}
                >
                  EDIT
                </button>

              </div>
            </article>
          );
        })}

        {(!filteredCustomers || filteredCustomers.length === 0) && (
          <div style={{ ...styles.customerCard, justifyContent: 'center', textAlign: 'center', fontWeight: 900 }}>
            {loading ? 'LOADING…' : 'NO CUSTOMERS FOUND.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
